import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Control from "../../common/Control";
import InfoBulle from "../../common/InfoBulle";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import { Pill } from "../../common/DataList";
import CartesRegimes from "./CartesRegimes";
import { creerOperateur } from "./operateursApi";
import { STATUTS_OPERATEUR, libelleStatut } from "./libellesOperateur";

// ============================================================
// Création d'un opérateur en modale (CE-1404). Seul le nom est obligatoire ;
// sans régime coché, la structure est un opérateur classique du répertoire
// (statut « Actif »), avec un régime elle naît « en préparation ». Si l'API
// répond 409 STRUCTURES_PROCHES, les structures proposées s'affichent :
// l'utilisateur en ouvre une, ou confirme la création (la même requête
// repart avec confirmerMalgreStructuresProches). Erreurs 400/419 sous les
// champs, refus 409 en bandeau. Le rattachement n'est pas saisi : l'API
// rattache toute structure à la Caf.
// ============================================================

/** Au survol du « i » du libellé : le sigle n'est ni obligatoire ni un identifiant. */
const AIDE_SIGLE =
  "Abréviation facultative, par exemple AREP : elle aide à retrouver la structure et à repérer un doublon.";

const FORMULAIRE_VIDE = { nom: "", sigle: "", regimes: [] };

const ModaleCreationOperateur = ({ ouvert, onFermer, onCreation }) => {
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [structuresProches, setStructuresProches] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  // La réponse d'une création partie juste avant une fermeture (par la page)
  // n'ouvre pas la fiche d'une modale qui n'existe plus.
  const ouvertRef = useRef(ouvert);
  useEffect(() => {
    ouvertRef.current = ouvert;
  }, [ouvert]);

  const fermer = () => {
    setFormulaire(FORMULAIRE_VIDE);
    setErreurs({});
    setRefus(null);
    setStructuresProches(null);
    onFermer();
  };

  const changer = (e) => {
    const { name, value } = e.target;
    setFormulaire((f) => ({ ...f, [name]: value }));
    setErreurs((er) => ({ ...er, [name]: undefined }));
    // Le formulaire a changé : les structures proches proposées ne valent
    // plus confirmation, la prochaine tentative repart sans confirmer.
    setStructuresProches(null);
    setRefus(null);
  };

  const basculerRegime = (regime) => {
    setFormulaire((f) => ({
      ...f,
      regimes: f.regimes.includes(regime)
        ? f.regimes.filter((r) => r !== regime)
        : [...f.regimes, regime],
    }));
    setStructuresProches(null);
    setRefus(null);
  };

  const enregistrer = (confirmer = false) => {
    const nom = formulaire.nom.trim();
    if (!nom) {
      setErreurs({ nom: "Saisissez le nom de la structure." });
      return;
    }
    const corps = { nom, regimes: formulaire.regimes };
    if (formulaire.sigle.trim()) corps.sigle = formulaire.sigle.trim();
    if (confirmer) corps.confirmerMalgreStructuresProches = true;

    setEnregistrement(true);
    setErreurs({});
    setRefus(null);
    creerOperateur(corps).then(
      (fiche) => {
        if (!ouvertRef.current) return;
        setEnregistrement(false);
        setFormulaire(FORMULAIRE_VIDE);
        setStructuresProches(null);
        onCreation(fiche);
      },
      (erreur) => {
        if (!ouvertRef.current) return;
        setEnregistrement(false);
        if (erreur.champs) {
          setErreurs(erreur.champs);
          // Une erreur sur un champ absent de la modale resterait invisible :
          // elle remonte en bandeau.
          const champsModale = ["nom", "sigle", "regimes"];
          const horsModale = Object.keys(erreur.champs).filter((c) => !champsModale.includes(c));
          if (horsModale.length) {
            setRefus(horsModale.map((c) => erreur.champs[c]).join(" "));
          }
        } else if (erreur.code === "STRUCTURES_PROCHES") {
          setStructuresProches(erreur.structures || []);
        } else {
          setRefus(erreur.message || "Une erreur est survenue");
        }
      }
    );
  };

  const soumettre = (e) => {
    e.preventDefault();
    enregistrer(!!structuresProches);
  };

  return (
    <ModaleLocale
      ouvert={ouvert}
      onFermer={fermer}
      titre="Créer un opérateur"
      sousTitre="Les sites et les calendriers s'ajoutent ensuite depuis sa fiche."
      taille="moyenne"
      idTitre="modale-creation-operateur"
      peutFermer={!enregistrement}
      pied={
        <>
          <p className="modale-operateur__aide-pied">
            Avec un régime, l'opérateur est créé « en préparation » : il ne reçoit aucune
            orientation avant sa mise en service.
          </p>
          <button type="button" className="btn btn-ghost" onClick={fermer} disabled={enregistrement}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-creation-operateur"
            className="btn btn-primary"
            disabled={enregistrement}
          >
            {structuresProches ? "Créer quand même" : "Créer l'opérateur"}
          </button>
        </>
      }
    >
      <form id="formulaire-creation-operateur" onSubmit={soumettre} noValidate>
        {refus && (
          <div className="alert alert-danger" role="alert">
            {refus}
          </div>
        )}

        <div className="modale-operateur__champs">
          <Control
            margin="my-0 modale-operateur__champ-large"
            label="Nom de la structure"
            name="nom"
            value={formulaire.nom}
            change={changer}
            error={erreurs}
            required
            inputProps={{ autoComplete: "off" }}
          />
          <Control
            margin="my-0"
            label={
              <>
                Sigle <InfoBulle texte={AIDE_SIGLE} />
              </>
            }
            name="sigle"
            value={formulaire.sigle}
            change={changer}
            error={erreurs}
            inputProps={{ autoComplete: "off" }}
          />
        </div>

        <fieldset className="modale-operateur__regimes">
          <legend className="modale-operateur__legende">Régimes exercés</legend>
          <CartesRegimes
            regimes={formulaire.regimes}
            onBasculer={basculerRegime}
            erreur={erreurs.regimes}
            idPrefix="creation-"
          />
          <p className="modale-operateur__aide">
            Sans régime, la structure est une simple entrée du répertoire, proposée dans les
            contrats d'engagement.
          </p>
        </fieldset>

        {structuresProches && (
          <div className="structures-proches" role="alert">
            <p className="structures-proches__titre">
              <i className="fa fa-exclamation-triangle" aria-hidden="true" />
              {structuresProches.length > 1
                ? "Des structures proches existent déjà. Ouvrez celle qui correspond plutôt que de créer un doublon, ou créez quand même."
                : "Une structure proche existe déjà. Ouvrez-la plutôt que de créer un doublon, ou créez quand même."}
            </p>
            <ul className="structures-proches__liste">
              {structuresProches.map((s) => (
                <li key={s.id}>
                  <span className="structures-proches__identite">
                    <span className="structures-proches__nom">{s.nom}</span>
                    <span className="structures-proches__detail">
                      {[s.sigle, s.ville].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </span>
                  <Pill
                    tone={(STATUTS_OPERATEUR[s.statut] || {}).tone || "neutral"}
                    icon={(STATUTS_OPERATEUR[s.statut] || {}).icon}
                  >
                    {libelleStatut(s.statut)}
                  </Pill>
                  <Link
                    to={`/operateurs/${s.id}`}
                    className="btn btn-ghost btn-sm"
                    onClick={fermer}
                  >
                    Ouvrir la fiche
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </ModaleLocale>
  );
};

export default ModaleCreationOperateur;
